import { Injectable, signal } from '@angular/core';

export type ToastType = 'error' | 'success' | 'info';

export interface ToastAction {
  label: string;
  /** Invoked on click; the toast dismisses itself right after. */
  run: () => void;
}

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

const DEFAULT_DURATION_MS = 5000;
const SUCCESS_DURATION_MS = 3000;
/** Actionable toasts linger longer — the reader needs time to decide to click. */
const ACTION_DURATION_MS = 8000;

/**
 * The dashboard had no global feedback mechanism — roughly 20 places caught
 * HTTP errors with only console.error, so a failed save/delete/reply gave
 * the operator zero visible signal. This, paired with the error interceptor
 * showing a toast on every HTTP failure, closes all of those at once.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private nextId = 0;
  private readonly toasts_ = signal<Toast[]>([]);
  readonly toasts = this.toasts_.asReadonly();

  show(message: string, type: ToastType = 'info', durationMs = DEFAULT_DURATION_MS, action?: ToastAction): void {
    const id = this.nextId++;
    this.toasts_.update((list) => [...list, { id, message, type, action }]);
    setTimeout(() => this.dismiss(id), durationMs);
  }

  error(message: string): void {
    this.show(message, 'error');
  }

  success(message: string): void {
    this.show(message, 'success', SUCCESS_DURATION_MS);
  }

  info(message: string, action?: ToastAction, durationMs?: number): void {
    this.show(message, 'info', durationMs ?? (action ? ACTION_DURATION_MS : DEFAULT_DURATION_MS), action);
  }

  runAction(toast: Toast): void {
    toast.action?.run();
    this.dismiss(toast.id);
  }

  dismiss(id: number): void {
    this.toasts_.update((list) => list.filter((t) => t.id !== id));
  }
}
